import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import { availableLanguagesForUser } from '@/common/constants'
import configurationServices from '@/configuration/services/services'
import { transformResponseLogisticsPreferences } from '@/configuration/transformers/logisticsPreferences/transformers'
import routes from '@/Router/routes.constants'

import {
  addStepToAllLanguages,
  getReturnInstructions,
  hasCompletedSteps,
  normalizeSteps,
  removeStepFromAllLanguages,
  updateStepText,
} from './helpers'
import { transformRequestSaveReturnInstruction, transformResponseReturnInstruction } from './transformers'

const useReturnInstructionSet = ({ id, storeId, supportedLanguages, setToastInfo }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const selectedInstruction = location?.state?.selectedInstruction
  const isNew = id === 'new' || id === 'duplicate'

  const languages = availableLanguagesForUser.filter(lang => supportedLanguages?.includes(lang))

  const {
    data: fetchedData,
    loading: fetching,
    resetData,
  } = useFetch(configurationServices.getReturnInstructionSet, {
    doInitialRequest: !isNew && !!storeId,
    params: { storeId, returnInstructionSetId: id },
    transformResponse: transformResponseReturnInstruction,
  })

  const [name, setNameState] = useState(selectedInstruction?.name || '')
  const [type, setTypeState] = useState(selectedInstruction?.type || '')
  const [steps, setSteps] = useState(getReturnInstructions(selectedInstruction?.steps, languages))
  const [hasChanges, setHasChanges] = useState(false)
  const { data: allDeliveryMethods, loading: loadingDeliveryMethods } = useFetch(
    configurationServices.getStoreLogisticsPreferences,
    {
      doInitialRequest: !isNew && !!storeId,
      params: { storeId },
      transformResponse: transformResponseLogisticsPreferences,
    }
  )

  const deliveryMethods =
    allDeliveryMethods?.delivery_methods?.filter(dm => fetchedData?.delivery_method_ids?.includes(dm.id)) || []

  useEffect(() => {
    if (fetchedData && !isNew) {
      const langs = availableLanguagesForUser.filter(lang => supportedLanguages?.includes(lang))

      setNameState(fetchedData.name || '')
      setTypeState(fetchedData.type || '')
      setSteps(getReturnInstructions(fetchedData.steps, langs))
    }
  }, [fetchedData, isNew, supportedLanguages])

  const { fetchData: createInstruction, loading: creating } = useFetch(
    configurationServices.createReturnInstructionSet,
    { doInitialRequest: false, transformRequest: transformRequestSaveReturnInstruction }
  )

  const { fetchData: editInstruction, loading: editing } = useFetch(configurationServices.editReturnInstructionSet, {
    doInitialRequest: false,
    transformRequest: transformRequestSaveReturnInstruction,
  })

  const setName = value => {
    setHasChanges(true)
    setNameState(value)
  }

  const setType = value => {
    setHasChanges(true)
    setTypeState(value)
  }

  const isFormValid = () => {
    return !!name && !!type && hasCompletedSteps(steps, languages)
  }

  const onSaveChanges = async () => {
    const payload = { name, type, steps: normalizeSteps(steps) }

    try {
      if (isNew) {
        await createInstruction({ storeId, payload })
        setToastInfo({ show: true, message: t('Instrucciones de devolución creadas'), type: 'success' })
        navigate(routes.configurationPage.logistics.returnInstructions)
      } else {
        await editInstruction({ storeId, returnInstructionSetId: id, payload })
        setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
        setHasChanges(false)
        resetData()
      }
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const revertChanges = () => {
    const source = fetchedData || selectedInstruction
    setNameState(source?.name || '')
    setTypeState(source?.type || '')
    setSteps(getReturnInstructions(source?.steps, languages))
    setHasChanges(false)
  }

  const onAddStep = () => {
    setHasChanges(true)
    setSteps(addStepToAllLanguages(steps, languages))
  }

  const onRemoveStep = stepId => {
    setHasChanges(true)
    setSteps(removeStepFromAllLanguages(steps, languages, stepId))
  }

  const onChangeStep = (value, language, stepId) => {
    setHasChanges(true)
    setSteps(prev => updateStepText(prev, language, stepId, value))
  }

  return {
    name,
    setName,
    type,
    setType,
    steps,
    onAddStep,
    onRemoveStep,
    onChangeStep,
    hasChanges,
    isFormValid,
    onSaveChanges,
    revertChanges,
    creating,
    editing,
    fetching: !isNew && (fetching || loadingDeliveryMethods),
    deliveryMethods,
    languages,
    isNew,
  }
}

export default useReturnInstructionSet
